import { doc, getDoc, collection, query, orderBy, limit, getDocs } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../config/firebase';

// ═══════════════ GET WALLET BALANCE ═══════════════
// Normalised reader: handles both the cloud-function shape ({balance, balanceBySource:{refund,giftCard,adminCredit,promo}})
// AND the legacy flat shape ({totalBalance, refundCredits, giftCardCredits, adminCredits, promoCredits}).
// Always returns the same flat shape to callers, so UI code keeps working unchanged.
export async function getWalletBalance(userId) {
  const walletRef = doc(db, 'wallets', userId);
  const walletDoc = await getDoc(walletRef);

  if (!walletDoc.exists()) {
    return {
      totalBalance: 0,
      refundCredits: 0,
      giftCardCredits: 0,
      adminCredits: 0,
      promoCredits: 0,
      earningsCredits: 0,
    };
  }

  const data = walletDoc.data();
  const bySource = data.balanceBySource || {};

  // Prefer new shape (balance + balanceBySource), fall back to legacy fields.
  return {
    ...data, // preserve any extra fields callers might rely on
    totalBalance: typeof data.balance === 'number' ? data.balance : (data.totalBalance || 0),
    refundCredits: typeof bySource.refund === 'number' ? bySource.refund : (data.refundCredits || 0),
    giftCardCredits: typeof bySource.giftCard === 'number' ? bySource.giftCard : (data.giftCardCredits || 0),
    adminCredits: typeof bySource.adminCredit === 'number' ? bySource.adminCredit : (data.adminCredits || 0),
    promoCredits: typeof bySource.promo === 'number' ? bySource.promo : (data.promoCredits || 0),
    earningsCredits: typeof bySource.earnings === 'number' ? bySource.earnings : (data.earningsCredits || 0),
  };
}

// ═══════════════ GET WALLET TRANSACTIONS ═══════════════
export async function getWalletTransactions(userId, limitCount = 20) {
  const txRef = collection(db, 'wallets', userId, 'transactions');
  const q = query(txRef, orderBy('createdAt', 'desc'), limit(limitCount));
  const snapshot = await getDocs(q);

  return snapshot.docs.map((doc) => ({
    id: doc.id,
    ...doc.data(),
  }));
}

// ═══════════════ REQUEST CASHOUT ═══════════════
export async function requestCashout(userId) {
  const cashout = httpsCallable(functions, 'requestWalletCashout');
  const result = await cashout({ userId });
  return result.data;
}

// ═══════════════ REDEEM GIFT CARD ═══════════════
export async function redeemGiftCard(userId, code) {
  const redeem = httpsCallable(functions, 'redeemGiftCard');
  const result = await redeem({ userId, code });
  return result.data;
}

// ═══════════════ DRIVER PER-TRIP INVOICE ═══════════════
// Returns { url, path, expiresAt } — a short-lived signed URL to the trip's
// earnings statement / tax invoice PDF (generated on demand, cached server-side).
export async function getDriverTripInvoice(bookingId) {
  const fn = httpsCallable(functions, 'getDriverTripInvoice');
  const result = await fn({ bookingId });
  return result.data;
}

// Trigger a browser download from a signed URL.
export function downloadFromSignedUrl(url, filename) {
  const a = document.createElement('a');
  a.href = url;
  if (filename) a.download = filename;
  a.target = '_blank';
  a.rel = 'noopener';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
}